import {AfterViewInit, Component, effect, forwardRef, input, signal, viewChild} from '@angular/core';
import {WjChartAnnotationModule, WjFlexChartAnnotationLayer} from '@mescius/wijmo.angular2.chart.annotation';
import {WjChartModule, WjFlexChart, WjFlexChartAxis, WjFlexChartSeries} from '@mescius/wijmo.angular2.chart';
import {Wijmo} from '../../lib/wjgrid-utils';
import {WjChartInteractionModule} from '@mescius/wijmo.angular2.chart.interaction';
import {EventArgs, Globalize} from '@mescius/wijmo';
import {HitTestInfo} from '@mescius/wijmo.chart';
import {AnnotationAttachment, AnnotationPosition, Line, Text} from '@mescius/wijmo.chart.annotation';
import {partition} from 'es-toolkit';
import {Stdlib} from '../../lib/stdlib';
import {IntersectionObserverDirective} from '../../directives/intersection-observer.directive';
import {
    SEGMENT_DATA_OBSERVER_HOST,
    SegmentDataObserverHost,
    SegmentDataObserverHostBase
} from '../../directives/segment-data-observer.token';
import {WjCharts} from '../../lib/wjcharts';

export interface ChartDataPoint {
    account: string;
    keyAccount: boolean | undefined;
    scoreAts: number;
    new_target_rate_change?: number;
    finalPilStatus?: string | null;
}

export interface ChartTieringRange {
    toValue: number;
    tierName: string;
}

export interface ChartAreaMinMax {
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
}

const InitialChartAreaMinMax: ChartAreaMinMax = { xMin: 0, xMax: 1, yMin: 0, yMax: 1 };

export const Series1Style = { stroke: 'orange', fill: 'red', strokeWidth: 1, ry: 3, rx: 3 };
export const Series2Style = {stroke: 'darkblue', fill: 'lightblue', strokeWidth: 1, ry: 3, rx: 3};
export const PilStyle = { stroke: 'red', strokeWidth: 2,  };
export const TextAnnotationStyle = { 'font-size': '14px', 'font-weight': 'normal', fill: 'red', 'abc': 'def' };

@Component({
    selector: 'app-ats-scatter-chart',
    imports: [
        WjChartModule, WjChartAnnotationModule, WjChartInteractionModule, IntersectionObserverDirective
    ],
    templateUrl: './ats-scatter-chart.component.html',
    styleUrl: './ats-scatter-chart.component.scss',
    // ...
    providers: [
        { provide: SEGMENT_DATA_OBSERVER_HOST, useExisting: forwardRef(() => AtsScatterChartComponent) }
    ]
})
export class AtsScatterChartComponent extends SegmentDataObserverHostBase {

    data = input<ChartDataPoint[]>([]);
    bindingY = input<string>('targetRateChange');
    ranges = input<ChartTieringRange[]>([]);
    noDataAlertText = input<string>('No data');

    protected readonly Wijmo = Wijmo;
    protected readonly Series1Style = Series1Style;
    protected readonly Series2Style = Series2Style;
    protected readonly PilStyle = PilStyle;

    private readonly atsScatterChartAxisMinMax = signal(InitialChartAreaMinMax);
    private readonly atsScatterChartTieringRangeAnnotations = viewChild<WjFlexChartAnnotationLayer>('atsScatterChartTieringRangeAnnotations');

    private readonly atsScatterChart = viewChild<WjFlexChart>('atsScatterChart');
    private readonly atsKeyAccountsSeries = viewChild<WjFlexChartSeries>('keyAccounts');
    private readonly atsNonKeyAccountsSeries = viewChild<WjFlexChartSeries>('nonKeyAccounts');

    private readonly axisY = viewChild<WjFlexChartAxis>('axisY');
    private readonly axisX = viewChild<WjFlexChartAxis>('axisX');

    protected readonly hasData = signal(true);

    protected readonly ttc = this.tooltipContent.bind(this);

    constructor() {
        super();
        effect(() => this.assignChartData())
        effect(() => this.positionScatterChartTieringRangeMarkers());
    }

    private assignChartData() {
        const [kaData, nonKaData] = partition(this.data(), (account) => !!account.keyAccount);

        const chartDataYValues = this.data().map((d: any) => d[this.bindingY()] ?? 0);
        const chartDataXValues = this.data().map((d: any) => d.scoreAts).filter((v) => v != undefined);
        const tieringRangesXValues = this.ranges().flatMap((r: any) => [r.toValue])
            .filter((v) => v != undefined);

        const maxY = Math.max(...chartDataYValues);
        const minY = Math.min(...chartDataYValues);
        const maxX = Math.max(...chartDataXValues, ...tieringRangesXValues);
        const minX = Math.min(...chartDataXValues, ...tieringRangesXValues);

        Stdlib.let(this.axisY(), ax => { ax.max = maxY * 1.2; ax.min = Math.min(minY * 1.2, 0); });
        Stdlib.let(this.axisX(), ax => { ax.max = maxX * 1.2; ax.min = minX * 0.8; });
        WjCharts.updateSeriesItemsSource(this.atsKeyAccountsSeries(), kaData);
        WjCharts.updateSeriesItemsSource(this.atsNonKeyAccountsSeries(), nonKaData);

        this.hasData.set(this.data().some((d: any) => d[this.bindingY()] != undefined));
    }

    onAccountDataChanged() {
        this.assignChartData();
    }

    protected onChartInitialized(atsScatterChart: WjFlexChart) {
        // setTimeout(() => this.assignChartData(), 1000);
    }

    onScatterChartRangeChanged(chart: WjFlexChart, xAxis: WjFlexChartAxis, yAxis: WjFlexChartAxis, event: EventArgs) {
        this.atsScatterChartAxisMinMax.set({ xMin: xAxis.actualMin, xMax: xAxis.actualMax, yMin: yAxis.actualMin, yMax: yAxis.actualMax } );
    }

    tooltipContent(hti: HitTestInfo) {
        const data = hti.item as ChartDataPoint;
        const rateChange = Globalize.formatNumber((data as any)[this.bindingY()] ?? 0, 'P1');
        const ats = Globalize.formatNumber(data.scoreAts ?? 0, 'P1');
        return `<b>Account: </b>${data.account}</br><b>Account Tiering Score</b>: ${ats}</br><b>Target Rate Change</b>: ${rateChange}`;
    }

    positionScatterChartTieringRangeMarkers() {
        // access the minMax signal to make sure, the Angular effect() is triggered when the value changes
        const minMax = this.atsScatterChartAxisMinMax();
        const annotationLayer = this.atsScatterChartTieringRangeAnnotations();
        if (!annotationLayer) return;

        // strictly speaking - we only need to react if the minMax *y* values change - TODO....
        annotationLayer.items.clear();
        const ranges = this.ranges();

        ranges.forEach((r, index) => {
            const isFirst = index === 0;
            const isLast = index === ranges.length - 1;

            if (index < ranges.length - 1) {
                const line = new Line({
                    attachment: AnnotationAttachment.DataCoordinate,
                    position: AnnotationPosition.Center,
                    // content: r.tier,
                    start: {x: r.toValue, y: minMax.yMin},
                    end: {x: r.toValue, y: minMax.yMax},
                    style: this.PilStyle
                });
                annotationLayer.items.push(line);
            }

            const xPosition = isFirst ? r.toValue : ranges[index - 1].toValue;
            const xOffset = isFirst ? -30 : isLast ? 30 : 2;

            const lbl = new Text({
                attachment: AnnotationAttachment.DataCoordinate,
                position: (index === 0) ? AnnotationPosition.Left : AnnotationPosition.Right,
                text: (r as any).tierName,
                point: {x: xPosition, y: minMax.yMax },
                offset: {x: xOffset, y: 10},
                style: TextAnnotationStyle
            });
            annotationLayer.items.push(lbl);
        });
    }
}
